import type { ClassificationNodeId } from '@/interfaces/Classification.interface';
import {
    CoreApiVersionPath,
    RosaCoreApiService,
    convertPaginatedRequestParamsToString,
    type CoreApiPaginatedRequestParams,
    type CoreApiPaginatedResponse
} from '@/rosa';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type {
    ConflictResolutionStrategy,
    DocumentManagementFile,
    DocumentManagementFileId,
    DocumentManagementFileTypeId
} from '../../interfaces/DocumentManagement.interface';
import type { UserId } from '../../interfaces/User.interface';

export interface DeleteFileDto {
    isDynamicSelectionEnabled: boolean;
    filters: SearchBodyDto;
    filesExcludedFromDynamicSelection: DocumentManagementFileId[];
    clientSelectedFileIds: DocumentManagementFileId[];
}

export interface SearchBodyDto {
    classificationNodeId: ClassificationNodeId;
    exactName?: string;
}

@Injectable({
    providedIn: 'root'
})
export class AdvizeoCoreDocumentManagementProvider {
    private _rosaCoreApiService = inject(RosaCoreApiService);

    uploadFile$(classificationNodeId: ClassificationNodeId, file: File, conflictResolutionStrategyCode: ConflictResolutionStrategy) {
        interface RouteRequest {
            classificationNodeId: ClassificationNodeId;
            conflictResolutionStrategy: ConflictResolutionStrategy;
        }

        const body: RouteRequest = {
            classificationNodeId,
            conflictResolutionStrategy: conflictResolutionStrategyCode
        };
        return this._rosaCoreApiService.postToFileBody(`${CoreApiVersionPath.V4}document-management/files`, file, body);
    }

    fetchFiles$(
        classificationNodeId: ClassificationNodeId,
        paginationParams: CoreApiPaginatedRequestParams,
        exactName?: string
    ): Observable<CoreApiPaginatedResponse<DocumentManagementFile>> {
        type RouteResponse = CoreApiPaginatedResponse<{
            fileId: DocumentManagementFileId;
            fileTypeId: DocumentManagementFileTypeId;
            extension: string;
            fileName: string;
            url: string;
            size: number;
            lastUpdatedBy: UserId;
            lastUpdatedAt: string;
        }>;

        const params = convertPaginatedRequestParamsToString(paginationParams);
        return this._rosaCoreApiService.post<RouteResponse>(`${CoreApiVersionPath.V4}document-management/files/search?${params}`, {
            classificationNodeId,
            exactName
        });
    }

    updateFile$(fileId: DocumentManagementFileId, newName: string): Observable<void> {
        return this._rosaCoreApiService.patch(`${CoreApiVersionPath.V4}document-management/files/${fileId}/filename`, {
            newName
        });
    }

    deleteFiles$(deleteFileDto: DeleteFileDto): any {
        return this._rosaCoreApiService.post(`${CoreApiVersionPath.V4}document-management/files/delete`, deleteFileDto);
    }

    fetchQuota$(): Observable<{ limitInKB: number; currentInKB: number }> {
        interface RouteResponse {
            currentKB: number;
            maximumKB: number;
        }
        return this._rosaCoreApiService.get<RouteResponse>(`${CoreApiVersionPath.V4}document-management/quota`).pipe(
            map(({ currentKB, maximumKB }) => {
                return { currentInKB: currentKB, limitInKB: maximumKB };
            })
        );
    }
}
